import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Rocket } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { INDUSTRIES, LOCATIONS } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const BUDGETS = [
    "$50 – Starter Boost · 1,000+ views",
    "$100 – Growth Accelerator · 2,500+ views",
    "$200 – Momentum · 6,000+ views",
    "$350 – Power · 11,000+ views",
    "$500 – Viral · 18,000+ views",
    "$750 · 28,000+ views",
    "$995 · 40,000+ views",
    "$1,745 · 70,000+ views",
    "$2,485 · 100,000+ views",
    "$3,975 · 160,000+ views",
    "$5,945 · 240,000+ views",
    "$7,425 · 300,000+ views",
    "$10,000 · 405,000+ views",
    "Custom / not sure",
];

export default function ScheduleCampaign() {
    const [form, setForm] = useState({
        business_name: "",
        name: "",
        phone: "",
        email: "",
        industry: "",
        target_location: "",
        budget: "",
        additional_notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.industry || !form.budget || !form.target_location) {
            toast.error("Please complete all required fields.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/leads/advertiser", form);
            toast.success("Your campaign request was received. We'll reach out within 24 hours.");
            setSubmitted(true);
            setForm({ business_name: "", name: "", phone: "", email: "", industry: "", target_location: "", budget: "", additional_notes: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white" data-testid="schedule-page">
            <section className="bg-[#0A192F] pt-32 md:pt-40 pb-16 relative overflow-hidden bg-grain">
                <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D4AF37]/15 blur-3xl" />
                <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">Schedule Campaign</div>
                    <h1 className="font-display text-4xl md:text-6xl font-bold text-white max-w-3xl leading-tight">
                        Tell us about your <span className="text-gold-gradient">campaign.</span>
                    </h1>
                    <p className="text-white/70 mt-4 max-w-2xl">
                        Fill in the details below. We'll respond within 24 hours with a tailored proposal.
                    </p>
                </div>
            </section>

            <section className="py-16 md:py-20">
                <div className="max-w-3xl mx-auto px-6 md:px-8">
                    {submitted ? (
                        <div className="bg-[#0A192F] text-white rounded-3xl p-12 text-center" data-testid="schedule-success">
                            <Rocket size={56} weight="duotone" color="#D4AF37" className="mx-auto" />
                            <h3 className="font-display text-3xl font-bold mt-4">Campaign request received!</h3>
                            <p className="text-white/70 mt-3 max-w-md mx-auto">Our team will contact you within 24 hours with a tailored proposal and pricing.</p>
                            <button onClick={() => setSubmitted(false)} className="mt-6 px-6 py-3 rounded-full bg-[#D4AF37] text-[#0A192F] font-semibold" data-testid="schedule-submit-another">
                                Submit another request
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="bg-white rounded-3xl p-8 md:p-10 border border-slate-200 shadow-xl space-y-5" data-testid="schedule-form">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="biz" className="text-[#0A192F] font-semibold">Business Name *</Label>
                                    <Input id="biz" required value={form.business_name} onChange={update("business_name")} className="mt-1.5" data-testid="schedule-input-business" />
                                </div>
                                <div>
                                    <Label htmlFor="contact" className="text-[#0A192F] font-semibold">Contact Name *</Label>
                                    <Input id="contact" required value={form.name} onChange={update("name")} className="mt-1.5" data-testid="schedule-input-name" />
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="phone" className="text-[#0A192F] font-semibold">Phone *</Label>
                                    <Input id="phone" required value={form.phone} onChange={update("phone")} className="mt-1.5" data-testid="schedule-input-phone" />
                                </div>
                                <div>
                                    <Label htmlFor="email" className="text-[#0A192F] font-semibold">Email *</Label>
                                    <Input id="email" type="email" required value={form.email} onChange={update("email")} className="mt-1.5" data-testid="schedule-input-email" />
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-[#0A192F] font-semibold">Industry *</Label>
                                    <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                                        <SelectTrigger className="mt-1.5" data-testid="schedule-select-industry">
                                            <SelectValue placeholder="Select industry" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {INDUSTRIES.map((i) => (
                                                <SelectItem key={i.name} value={i.name}>{i.name}</SelectItem>
                                            ))}
                                            <SelectItem value="Other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-[#0A192F] font-semibold">Target Location *</Label>
                                    <Select value={form.target_location} onValueChange={(v) => setForm({ ...form, target_location: v })}>
                                        <SelectTrigger className="mt-1.5" data-testid="schedule-select-location">
                                            <SelectValue placeholder="Select location" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {LOCATIONS.map((l) => (
                                                <SelectItem key={l.name} value={l.name}>{l.flag} {l.name}</SelectItem>
                                            ))}
                                            <SelectItem value="Nationwide">Nationwide</SelectItem>
                                            <SelectItem value="Custom">Custom — I'll specify in notes</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label className="text-[#0A192F] font-semibold">Budget *</Label>
                                <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                                    <SelectTrigger className="mt-1.5" data-testid="schedule-select-budget">
                                        <SelectValue placeholder="Select budget" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {BUDGETS.map((b) => (
                                            <SelectItem key={b} value={b}>{b}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="notes" className="text-[#0A192F] font-semibold">Additional Notes</Label>
                                <Textarea id="notes" rows={4} value={form.additional_notes} onChange={update("additional_notes")} className="mt-1.5" data-testid="schedule-input-notes" placeholder="Tell us about your campaign goals, creative, target audience..." />
                            </div>
                            <Button type="submit" disabled={submitting} className="w-full bg-[#D4AF37] hover:bg-[#e3c14a] text-[#0A192F] font-semibold py-6 rounded-full text-base flex items-center justify-center gap-2" data-testid="schedule-submit-button">
                                {submitting ? "Submitting..." : <>Launch My Campaign <CheckCircle size={18} weight="fill" /></>}
                            </Button>
                            <p className="text-xs text-slate-500 text-center">By submitting, you agree to be contacted by the CashFlow Ads team within 24 hours.</p>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
}
